export const FRAME_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";

export interface FrameScrollMessage {
	scrollTop: number;
	scrollHeight: number;
	clientHeight: number;
}

export function isScrolledToBottom(message: FrameScrollMessage) {
	//A frame with no height is hidden or not laid out yet, so it cannot have been read.
	//Optional chaining because a document's own scripts can post whatever they like to us
	if (!message?.clientHeight) return false;
	const scrollPos = message.scrollHeight - Math.round(message.scrollTop);
	return scrollPos <= message.clientHeight + 1;
}

export function buildFrameSrcDoc(document: string, allowScripts: boolean) {
	const content = allowScripts ? (document ?? "") : stripScripts(document ?? "");
	const injected = `<base target="_blank">${scrollTracking}`;

	if (/<html[\s>]/i.test(content)) {
		//Full page: keep it as is and inject at the top of the head so document css/js wins over ours
		if (/<head[^>]*>/i.test(content)) {
			return content.replace(/<head[^>]*>/i, (tag) => tag + injected);
		}
		return content.replace(/<html[^>]*>/i, (tag) => `${tag}<head>${injected}</head>`);
	}
	return `<!DOCTYPE html><html><head><meta charset="utf-8">${injected}</head><body>${content}</body></html>`;
}

/**
 * The frame is cross origin, so nothing outside it can read its scroll position: it has to report
 * its own. The style keeps the page itself as the scrolling element, otherwise a document that
 * scrolls its own body reports a full page height and counts as read before it has been scrolled.
 */
const scrollTracking = `<style>html,body{height:auto!important;max-height:none!important;overflow:visible!important}</style><script>
(function () {
	function report() {
		var el = document.scrollingElement || document.documentElement;
		parent.postMessage({ scrollTop: el.scrollTop, scrollHeight: el.scrollHeight, clientHeight: el.clientHeight }, "*");
	}
	addEventListener("scroll", report, { capture: true, passive: true });
	addEventListener("resize", report);
	//Fires once images are in, so a document that needs no scrolling counts as read
	addEventListener("load", report);
})();
</script>`;

/** Removes the document's own scripts. Our injected tracking script is added after this runs. */
function stripScripts(document: string) {
	return document.replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, "").replace(/<script\b[^>]*\/>/gi, "");
}
